import { AHTextFonts, Box, useLayoutContext, useTheme } from "arkh";
import React, { useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { graphql, useFragment } from "react-relay";

import * as d from "d3-scale";

import * as shape from "d3-shape";
import Svg, { Line, Path, Text } from "react-native-svg";
import { IssueStatsTTRGraph_IssueStats$key } from "./__generated__/IssueStatsTTRGraph_IssueStats.graphql";
import { Header } from "../RoutineOccasion/RoutineDetail/Header";
import { FormattedMessage, useIntl } from "react-intl";

export function IssueStatsTTRGraph(props: {
  issueStats: IssueStatsTTRGraph_IssueStats$key;
  rounded?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment IssueStatsTTRGraph_IssueStats on IssueStats {
        timeToResolveBucketed(fromDate: $fromDate) {
          bucketTimeSeconds
          counts
          total
        }
      }
    `,
    props.issueStats
  );

  const ul = useLayoutContext();
  const [width, setWidth] = useState(
    Dimensions.get("window").width - ul.space.medium * 2 - ul.space.xsmall * 2
  );
  const theme = useTheme();
  const height = 300;
  const intl = useIntl();

  if (data.timeToResolveBucketed.total < 10) {
    return null;
  }

  const paddingHorizontal = ul.space.large;
  const paddingVertical = ul.space.large;
  const scaleX = d.scaleLinear(
    [0, 80],
    [paddingHorizontal * 2, width - paddingHorizontal]
  );

  const scaleY = d.scaleLinear(
    [0, 1],
    [height - paddingVertical, paddingVertical]
  );

  const dd = data.timeToResolveBucketed.counts.map((y, index) => ({
    y: y / data.timeToResolveBucketed.total,
    x: Math.ceil(index * data.timeToResolveBucketed.bucketTimeSeconds) / 3600,
  }));

  const line = shape
    .line<(typeof dd)[number]>()
    .curve(shape.curveBasis)
    .x((p) => scaleX(p.x))
    .y((p) => scaleY(p.y))(dd);

  return (
    <Box spaceBottom="medium">
      <Box spaceHorizontal={props.rounded ? "none" : "medium"}>
        <Header
          icon="deadline"
          subtitle={
            <FormattedMessage
              defaultMessage="Share of issues resolved within a given time period"
              description="Issue Stats Time To Resolve Graph subtitle"
            />
          }
        >
          <FormattedMessage
            defaultMessage="Time to Resolve"
            description="Issue Stats Time To Resolve Graph title"
          />
        </Header>
      </Box>
      <Box
        color="surface"
        cornerRadius={props.rounded === true ? "medium" : "none"}
        style={{ position: "relative" }}
      >
        <View
          style={{ flex: 1 }}
          onLayout={(e) => {
            setWidth(e.nativeEvent.layout.width);
          }}
        >
          <Svg height={height} width={width}>
            {scaleY.ticks(5).map((tick, i) => {
              const y = scaleY(1 - tick);
              return (
                <Text
                  key={i + ""}
                  dy={height - y}
                  x={paddingHorizontal}
                  fill="#000"
                  textAnchor="middle"
                  fontFamily={AHTextFonts.MEDIUM as any}
                  fontSize={8}
                >
                  {tick * 100 + "%"}
                </Text>
              );
            })}
            {[0, 24, 48, 72].map((tick, i) => {
              const y = scaleY(0);
              return (
                <Text
                  key={i + ""}
                  y={y}
                  x={scaleX(tick) + ul.space.xsmall}
                  textAnchor="start"
                  fill="#000"
                  fontFamily={AHTextFonts.MEDIUM as any}
                  fontSize={8}
                >
                  {intl.formatMessage(
                    {
                      defaultMessage:
                        "within {value} {value, plural, =1 {hour} other {hours}}",
                    },
                    {
                      value: tick === 0 ? 1 : tick,
                    }
                  )}
                </Text>
              );
            })}

            {line ? (
              <Path
                d={line}
                stroke={theme.primary}
                fill="none"
                strokeWidth={2}
              />
            ) : null}
            {[0, 24, 48, 72].map((tick, i) => {
              return (
                <Line
                  key={i + ""}
                  y1={scaleY(0)}
                  y2={scaleY(1)}
                  x1={scaleX(tick)}
                  strokeWidth={StyleSheet.hairlineWidth}
                  stroke="#bbb"
                  x2={scaleX(tick)}
                />
              );
            })}
          </Svg>
        </View>
      </Box>
    </Box>
  );
}
